<template>
  <div class="chat-composer" :class="{ 'chat-composer--compact': compact }">
    <form class="chat-composer__form" @submit.prevent="handleSubmit">
      <div class="chat-composer__input-wrap">
        <div class="chat-composer__icon">
          <BaseIcon name="wand" size="16" />
        </div>
        <textarea
          ref="textareaRef"
          v-model="value"
          class="chat-composer__input"
          :placeholder="placeholder"
          :disabled="sending"
          rows="1"
          @keydown.enter.exact.prevent="handleSubmit"
          @input="autoresize"
        />
        <button
          type="submit"
          class="chat-composer__submit"
          :disabled="!canSubmit"
          :aria-label="$t('home.chat.send')"
        >
          <BaseIcon
            v-if="!sending"
            name="arrow-right"
            size="14"
            rotate="-90"
          />
          <LoadingSpinner v-else color="light" />
        </button>
      </div>
    </form>

    <div v-if="!compact && suggestions.length" class="chat-composer__suggestions">
      <button
        v-for="(suggestion, idx) in suggestions"
        :key="idx"
        type="button"
        class="chat-composer__chip"
        :disabled="sending"
        @click="handleChip(suggestion)"
      >
        {{ suggestion }}
      </button>
    </div>
  </div>
</template>

<script>
  import { ref, computed, nextTick, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';

  export default {
    name: 'HomeChatComposer',

    components: { BaseIcon, LoadingSpinner },

    props: {
      placeholder: { type: String, default: 'Vraag iets over je workspace...' },
      sending: { type: Boolean, default: false },
      suggestions: { type: Array, default: () => [] },
      compact: { type: Boolean, default: false },
      autofocus: { type: Boolean, default: false },
    },

    emits: ['submit'],

    setup(props, { emit }) {
      const value = ref('');
      const textareaRef = ref(null);

      const canSubmit = computed(() => value.value.trim().length > 0 && !props.sending);

      const autoresize = () => {
        const el = textareaRef.value;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
      };

      const handleSubmit = () => {
        if (!canSubmit.value) return;
        const text = value.value.trim();
        emit('submit', text);
        value.value = '';
        nextTick(autoresize);
      };

      const handleChip = (text) => {
        emit('submit', text);
      };

      watch(
        () => props.autofocus,
        (v) => {
          if (v) nextTick(() => textareaRef.value?.focus());
        },
        { immediate: true }
      );

      return {
        value,
        textareaRef,
        canSubmit,
        autoresize,
        handleSubmit,
        handleChip,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';

  .chat-composer {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    width: 100%;

    &__form {
      width: 100%;
    }

    &__input-wrap {
      position: relative;
      display: flex;
      align-items: flex-end;
      background: var(--slate-25, #fcfcfd);
      border: 1px solid var(--slate-200);
      border-radius: 1rem;
      padding: 0.6875rem 0.6875rem 0.6875rem 0.875rem;
      gap: 0.5rem;
      transition: all 0.15s ease;
      box-shadow:
        0 1px 2px rgb(15 23 42 / 0.05),
        inset 0 1px 0 rgb(255 255 255 / 0.7);

      &:focus-within {
        border-color: var(--brand-300);
        background: #ffffff;
        box-shadow:
          0 2px 6px rgb(15 23 42 / 0.08),
          0 0 0 3px rgb(59 130 246 / 0.12);
      }
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      border-radius: 0.5rem;
      background: linear-gradient(135deg, var(--brand-50), var(--violet-50, #f5f3ff));
      color: var(--brand-600);
      flex-shrink: 0;
      align-self: center;
    }

    &__input {
      flex: 1;
      min-width: 0;
      background: transparent;
      border: none;
      outline: none;
      font-size: 0.9375rem;
      line-height: 1.5;
      color: var(--slate-900);
      resize: none;
      max-height: 160px;
      min-height: 1.75rem;
      padding: 0.25rem 0.125rem 0.25rem 0;
      font-family: inherit;

      &::placeholder {
        color: var(--slate-400);
      }

      &:disabled {
        opacity: 0.6;
      }
    }

    &__submit {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border-radius: 0.625rem;
      border: none;
      background: linear-gradient(135deg, var(--brand-500), var(--brand-600));
      color: white;
      cursor: pointer;
      flex-shrink: 0;
      align-self: center;
      transition: all 0.15s ease;

      @include hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgb(37 99 235 / 0.25);
      }

      &:disabled {
        background: var(--slate-200);
        color: var(--slate-400);
        cursor: not-allowed;
        box-shadow: none;
        transform: none;
      }
    }

    &__suggestions {
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;

      @include lg-down {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding-bottom: 0.25rem;
        margin: 0 -0.125rem;
        scrollbar-width: none;
        -ms-overflow-style: none;

        &::-webkit-scrollbar {
          display: none;
        }
      }
    }

    &__chip {
      display: inline-flex;
      align-items: center;
      padding: 0.375rem 0.75rem;
      border-radius: 9999px;
      background: #ffffff;
      border: 1px solid var(--slate-200);
      color: var(--slate-700);
      font-size: 0.8125rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.15s ease;
      white-space: nowrap;

      @include hover {
        background: var(--brand-50);
        border-color: var(--brand-200);
        color: var(--brand-700);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    &--compact {
      .chat-composer__input-wrap {
        border-radius: 0.75rem;
        padding: 0.5625rem 0.5625rem 0.5625rem 0.75rem;
      }

      .chat-composer__icon {
        display: none;
      }

      .chat-composer__input {
        font-size: 0.875rem;
      }
    }

    @include lg-down {
      &__input-wrap {
        border-radius: 0.875rem;
      }

      &__submit {
        width: 32px;
        height: 32px;
      }
    }
  }
</style>
